import type { SessionContextValue } from "@/hooks/useSession";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export interface EnsureSessionReadyOptions {
  attempts?: number;
  initialDelayMs?: number;
  backoffFactor?: number;
}

/**
 * Hydrates /api/auth/me with bounded retry so post-login navigation only
 * continues once the session payload is actually available.
 */
export async function ensureSessionReady(
  session: Pick<SessionContextValue, "refreshSession">,
  options?: EnsureSessionReadyOptions,
): Promise<boolean> {
  const attempts = Math.max(1, options?.attempts ?? 4);
  const backoffFactor = Math.max(1, options?.backoffFactor ?? 1.8);
  let delayMs = Math.max(100, options?.initialDelayMs ?? 350);

  for (let index = 0; index < attempts; index += 1) {
    const ready = await session.refreshSession({ force: true });
    if (ready) {
      return true;
    }

    if (index < attempts - 1) {
      await sleep(delayMs);
      delayMs = Math.min(Math.round(delayMs * backoffFactor), 2_000);
    }
  }

  return false;
}
